import { escapeRegExp } from "@/utils/escape-regex-search-string";
import { buildNameSearchQuery } from "@/components/customers/helpers/build-name-search";
import { getMobileId } from "@/utils/clean-mobile-number";
import flattenDeep from "lodash/flattenDeep";

function partialRegex(term: string) {
  return { $regex: escapeRegExp(term.trim()), $options: "i" };
}

function parsePrefixedShortId(
  term: string,
  prefix: string
): number | undefined {
  const trimmed = term.trim();
  const prefixed = new RegExp(`^#?\\s*${prefix}-?(\\d+)$`, "i").exec(trimmed);
  if (prefixed) return parseInt(prefixed[1], 10);

  if (trimmed.startsWith("#")) {
    const num = Number(trimmed.slice(1).split(/\s+/)[0]);
    if (!Number.isNaN(num)) return num;
  }

  if (/^\d+$/.test(trimmed)) return parseInt(trimmed, 10);

  return undefined;
}

function buildContactNameClauses(
  term: string,
  firstNamePath: string,
  lastNamePath: string
) {
  const words = term.trim().split(/\s+/).filter(Boolean);
  if (words.length === 0) return [];

  return buildNameSearchQuery(words).map((clause) => {
    if ("$and" in clause) {
      return {
        $and: (clause.$and ?? []).map((part) => {
          const key = Object.keys(part)[0];
          const value = part[key as keyof typeof part];
          if (key === "data.firstName") {
            return { [firstNamePath]: value };
          }
          if (key === "data.lastName") {
            return { [lastNamePath]: value };
          }
          return part;
        }),
      };
    }

    const key = Object.keys(clause)[0];
    const value = clause[key as keyof typeof clause];
    if (key === "data.firstName") return { [firstNamePath]: value };
    if (key === "data.lastName") return { [lastNamePath]: value };
    return clause;
  });
}

function buildEmailClauses(term: string, emailPath: string) {
  const trimmed = term.trim();
  return [
    { [emailPath]: partialRegex(trimmed) },
    {
      [emailPath]: partialRegex(trimmed.split(/\s+/).join("")),
    },
  ];
}

function buildPhoneClauses(term: string, phonePath: string) {
  const trimmed = term.trim();
  const clauses: Record<string, unknown>[] = [
    { [phonePath]: partialRegex(trimmed) },
  ];

  const normalized = getMobileId(
    trimmed.charAt(0) === "0" ? trimmed.slice(1) : trimmed
  )
    .replace("+", "")
    .trim();

  if (normalized) {
    clauses.push({ [phonePath]: partialRegex(normalized) });
  }

  return clauses;
}

export function GlobalCustomersSearchQueryBuilder(
  term: string
): Record<string, unknown> {
  const trimmed = term.trim();
  if (!trimmed) return {};

  return {
    $or: flattenDeep([
      ...buildContactNameClauses(
        trimmed,
        "data.firstName",
        "data.lastName"
      ),
      ...buildEmailClauses(trimmed, "data.primaryEmail"),
      ...buildPhoneClauses(trimmed, "data.primaryMobile"),
      { "data.phones.phone": partialRegex(trimmed) },
    ]),
  };
}

export function GlobalEnquiriesSearchQueryBuilder(
  term: string
): Record<string, unknown> {
  const trimmed = term.trim();
  if (!trimmed) return {};

  const shortId = parsePrefixedShortId(trimmed, "enq");
  if (shortId != null) {
    return { shortId };
  }

  return {
    "data.notes": partialRegex(trimmed),
  };
}

export function GlobalQuotesSearchQueryBuilder(
  term: string
): Record<string, unknown> {
  const trimmed = term.trim();
  if (!trimmed) return {};

  const quoteShortId = parsePrefixedShortId(trimmed, "qte");
  if (quoteShortId != null) {
    return { shortId: quoteShortId };
  }

  return {
    $or: flattenDeep([
      { "data.title": partialRegex(trimmed) },
      ...buildContactNameClauses(
        trimmed,
        "contact.data.firstName",
        "contact.data.lastName"
      ),
      ...buildEmailClauses(trimmed, "contact.data.primaryEmail"),
      ...buildPhoneClauses(trimmed, "contact.data.primaryMobile"),
      { opportunityId: partialRegex(trimmed) },
    ]),
  };
}

export function GlobalBookingsSearchQueryBuilder(
  term: string
): Record<string, unknown> {
  const trimmed = term.trim();
  if (!trimmed) return {};

  const shortId = parsePrefixedShortId(trimmed, "bk");
  if (shortId != null) {
    return { shortId };
  }

  return {
    $or: flattenDeep([
      { id: partialRegex(trimmed) },
      { "enquiryData.customerName": partialRegex(trimmed) },
      ...buildEmailClauses(trimmed, "enquiryData.email"),
      ...buildPhoneClauses(trimmed, "enquiryData.mobileNumber"),
    ]),
  };
}

export function AccountingSearchQueryBuilder(
  term: string
): Record<string, unknown> {
  const t = term?.trim();
  if (!t) return {};

  const escaped = escapeRegExp(t);

  return {
    $or: [
      { bookingRef: { $regex: escaped, $options: "i" } },
      { id: { $regex: escaped, $options: "i" } },
      { reference: { $regex: escaped, $options: "i" } },
    ],
  };
}

export function parseEnquiryRefShortId(term: string): number | undefined {
  return parsePrefixedShortId(term, "enq");
}
